import React, { Component } from 'react';
import { ShieldAlert, Terminal, RefreshCw, Home, Copy, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

export default class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null, copied: false };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // You can log the error to an analytics service here
        console.error("[CubeGames ErrorBoundary Catch]", error, errorInfo);
        this.setState({ errorInfo });
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
        window.location.reload();
    };

    handleCopyError = () => {
        const errorReport = `
        === CUBEGAMES RUNTIME ERROR ===
        Error: ${this.state.error?.toString() || 'Unknown Error'}
        Stack: ${this.state.error?.stack || 'No Stack Available'}
        Component Stack: ${this.state.errorInfo?.componentStack || 'No Component Stack'}
        URL: ${window.location.href}
        Time: ${new Date().toISOString()}
        ===============================
        `;
        navigator.clipboard.writeText(errorReport);
        this.setState({ copied: true });
        setTimeout(() => this.setState({ copied: false }), 3000);
    };

    render() {
        if (this.state.hasError) {
            // Render beautiful custom cyberpunk fallback UI
            return (
                <div className="min-h-screen bg-[#050508] relative overflow-hidden flex flex-col items-center justify-center p-6 text-white font-sans selection:bg-cyber-pink selection:text-white">
                    {/* Retro grid pattern overlay */}
                    <div className="absolute inset-0 bg-cyber-grid opacity-[0.03] pointer-events-none"></div>

                    {/* Ambient light circles */}
                    <div className="absolute top-[20%] left-[10%] w-[350px] h-[350px] bg-cyber-pink/5 rounded-full blur-[110px] pointer-events-none"></div>
                    <div className="absolute bottom-[20%] right-[10%] w-[350px] h-[350px] bg-cyber-cyan/5 rounded-full blur-[110px] pointer-events-none"></div>

                    {/* Cyber board panel wrapping content */}
                    <div className="relative max-w-2xl w-full bg-[#090915]/80 border border-cyber-pink/20 rounded-[2rem] p-8 md:p-12 shadow-[0_0_50px_rgba(255,0,122,0.1)] text-center space-y-8 animate-in fade-in duration-500 scale-98 md:scale-100">
                        {/* Red neon top bar */}
                        <div className="absolute top-0 left-0 w-full h-[2.5px] bg-gradient-to-r from-cyber-pink to-cyber-purple shadow-[0_0_15px_#ff007a]"></div>

                        {/* Error Icon Indicator with pulse rings */}
                        <div className="relative mx-auto w-20 h-20 rounded-2xl bg-cyber-pink/10 border border-cyber-pink/30 flex items-center justify-center shadow-[0_0_30px_rgba(255,0,122,0.15)] group hover:scale-105 duration-300">
                            <span className="absolute inset-0 w-full h-full rounded-2xl bg-cyber-pink/10 animate-ping opacity-45"></span>
                            <ShieldAlert className="w-10 h-10 text-cyber-pink" />
                        </div>

                        {/* Text Title / Diagnostic Code */}
                        <div className="space-y-2.5">
                            <span className="text-[10px] font-cyber font-semibold tracking-[0.3em] text-cyber-pink uppercase block">APPLICATION ERROR</span>
                            <h1 className="text-3xl font-cyber font-semibold text-white tracking-tight uppercase">SOMETHING WENT WRONG</h1>
                            <p className="text-xs text-gray-500 max-w-md mx-auto leading-relaxed">
                                An unexpected error occurred in the application. Don't worry, your data is safe. Please try reloading the page or going back to the home page.
                            </p>
                        </div>

                        {/* Interactive Console error output panel */}
                        <div className="bg-[#040407] border border-white/5 rounded-2xl p-5 text-left font-mono text-xs text-gray-400 relative overflow-hidden">
                            <div className="flex items-center justify-between border-b border-white/5 pb-2 mb-3.5">
                                <span className="flex items-center gap-2 text-[9px] text-gray-500 tracking-wider uppercase font-cyber font-medium">
                                    <Terminal className="w-3.5 h-3.5 text-cyber-cyan" /> ERROR DETAILS
                                </span>
                                <span className="text-[8px] text-cyber-pink bg-cyber-pink/10 border border-cyber-pink/20 px-2 py-0.5 rounded font-cyber font-semibold">
                                    RUNTIME_ERROR
                                </span>
                            </div>
                            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-2 no-scrollbar scroll-smooth select-all">
                                <p className="text-cyber-cyan font-medium leading-none">Error Message:</p>
                                <p className="text-gray-300 text-[11px] leading-relaxed break-words font-semibold bg-white/[0.02] p-2.5 rounded-lg border border-white/5">
                                    {this.state.error?.toString()}
                                </p>
                                {this.state.errorInfo?.componentStack && (
                                    <div className="mt-3">
                                        <p className="text-cyber-purple font-medium leading-none mb-1">Stack Trace:</p>
                                        <pre data-lenis-prevent className="text-[9px] text-gray-500 leading-normal overflow-x-auto whitespace-pre p-2 bg-black rounded-lg border border-white/5 max-w-full font-mono">
                                            {this.state.errorInfo.componentStack}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Interactive operations and buttons */}
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5">
                            {/* Reset Storefront Button */}
                            <button
                                onClick={this.handleReset}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-cyber-pink to-cyber-purple text-white font-cyber font-semibold text-[11px] tracking-wider uppercase shadow-[0_0_20px_rgba(255,0,122,0.3)] hover:shadow-[0_0_30px_rgba(255,0,122,0.5)] transition-all active:scale-95 duration-200"
                            >
                                <RefreshCw className="w-4 h-4 animate-spin-slow" /> RELOAD PAGE
                            </button>

                            {/* Copy Debug Data */}
                            <button
                                onClick={this.handleCopyError}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-gray-300 hover:text-white transition-all text-[11px] font-cyber font-medium uppercase tracking-wider active:scale-95 duration-200"
                            >
                                {this.state.copied ? (
                                    <>
                                        <Check className="w-4 h-4 text-cyber-cyan" /> COPIED
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4" /> COPY ERROR
                                    </>
                                )}
                            </button>

                            {/* Return Home */}
                            <Link
                                to="/"
                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#090915] border border-cyber-cyan/30 text-cyber-cyan font-cyber font-semibold text-[11px] tracking-wider uppercase shadow-[0_0_15px_rgba(0,240,255,0.1)] hover:shadow-[0_0_25px_rgba(0,240,255,0.3)] hover:scale-[1.02] transition-all duration-200"
                            >
                                <Home className="w-4 h-4" /> GO HOME
                            </Link>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
